/* ----------------------------------------------------------------------------
 * batchPublishModel — the PURE, DOM-free decision core for Aura publish P1:
 * "push the next N drafts to <site>", Draft OR Publish, by chat, voice or tap.
 * Owner decision 10-10-2026 (docs/brain/governance/
 * owner-decision-aura-publish-choice-2026-10-10.md, verbatim there):
 *   - two options: draft or full publish; both in chat and voice;
 *   - the blog is NOT read out (API cost) — drafts are shown on screen as cards;
 *   - confirm by voice ("publish" / "push it") or by tick/tap on the Aura screen.
 *
 * SAFETY (locked here, exercised under node --test):
 *   - A bare "yes" acts ONLY as the confirmation of a read-back that already
 *     named the action, the site and the count ("Publish 'X' to booksesh.com?").
 *     With no action named, a bare "yes" gets "Publish or save as draft?".
 *     (Owner 10-10-2026: "once aura prompts and asks- shall i push it - prompt
 *     reply yes"; "push" means publish — decision file + owner report 18:48 UTC.)
 *   - Precedence is safety-first: NO > DRAFT > PUBLISH > bare YES. A mixed answer
 *     ("publish them as drafts") resolves to the SAFER draft.
 *   - Ambiguity re-asks once, then cancels (nothing is pushed).
 *   - The server is authoritative: the spoken read-back comes from the server's
 *     propose() response (site + count + effective mode), not from this file.
 *   - No drive/hands-free "parked" flag exists in the app today (checked
 *     10-10-2026); none is invented here. See DRIVE_MODE_NOTE.
 * -------------------------------------------------------------------------- */

import { isTurnClose } from '../wake/wakeMachine.ts';

export type BatchMode = 'draft' | 'publish';
export type BatchChannel = 'chat' | 'voice' | 'tap';

export const BATCH_DEFAULT_COUNT = 3;
export const BATCH_MAX_COUNT = 5;
export const BATCH_PUBLISH_MAX_REASKS = 1;

/** The mode question after a bare yes (owner wording: "publish/push it"). */
export const BATCH_ASK_MODE = 'Publish or save as draft?';
export const BATCH_REASK = "Sorry, I didn't catch that. Say publish, or draft.";
/** Re-ask after a read-back that named the action (a yes/no question). */
export const BATCH_REASK_CONFIRM = 'Say yes to publish, or no.';
/** Re-ask after a DRAFT read-back. */
export const BATCH_REASK_CONFIRM_DRAFT = 'Say yes to save it as a draft, or no.';
/** The confirm re-ask for the mode that was read back. */
export function batchConfirmReask(mode: BatchMode): string {
  return mode === 'publish' ? BATCH_REASK_CONFIRM : BATCH_REASK_CONFIRM_DRAFT;
}
export const BATCH_CANCEL_ACK = 'Okay, I’ll leave them as they are.';
/** Fable IVG C1: a mixed pick whose publish did not fully go through writes nothing. */
export const BATCH_WRITES_SKIPPED = 'The publish didn’t fully go through, so I haven’t written the new blogs.';
export const BATCH_NONE_READY = 'There are no ready drafts for that site right now.';
/** Undo-publish lines, chosen from the SERVER revert result (owner GO 22-10-2026 "fix BookedCentral
 *  too", sweep #5): ok+changed = reverted; ok+changed:false = it was already a draft; anything else
 *  = honest failure. BATCH_REVERTED keeps the original wording byte-identical. */
export const BATCH_REVERTED = 'Reverted to a draft.';
export const BATCH_REVERT_ALREADY_DRAFT = 'It was already a draft.';
export const BATCH_REVERT_FAILED = 'I couldn’t revert it to a draft. It may still be live.';
/** The server's candidates reason when the site has no SEO domain linked (publish target project_id NULL). */
export const BATCH_TARGET_UNLINKED_REASON = 'target_not_associated_with_domain';
export function batchTargetUnlinkedLine(siteName: string): string {
  return `${siteName} isn’t linked to a domain yet, so I can’t find its drafts. Link it in Connections.`;
}
export const BATCH_PICK_SITE = 'Which site? I’ve put the options on screen.';
export const BATCH_NO_SITE = 'I couldn’t find a connected WordPress site to push to.';
/** A site named in the command that is not under the domain being viewed (never a silent switch). */
export const BATCH_OUT_OF_DOMAIN = 'That site isn’t in the domain you’re viewing.';
/** Documented, not implemented: there is no drive/parked context flag in the app. */
export const DRIVE_MODE_NOTE =
  'No drive-mode / parked flag exists in the app (10-10-2026); voice publish is confirmed by the spoken verb plus the on-screen cards. A "ready to confirm when parked" hold needs a real driving signal first (owner decision).';

const NUMBER_WORDS: Readonly<Record<string, number>> = {
  one: 1, a: 1, an: 1, two: 2, couple: 2, three: 3, few: 3, four: 4, five: 5,
};

/** Answer normaliser: same shape as isTurnClose (drop apostrophes, non-alnum -> space). */
function norm(transcript: string): string {
  const s = (transcript ?? '')
    .replace(/['’‘`]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return s === '' ? '' : ' ' + s + ' ';
}

/** Ask normaliser: like norm() but keeps '.' and '/' INSIDE a token so a site
 *  name ("booksesh.com/partners") survives; sentence punctuation is dropped. */
function normAsk(transcript: string): string {
  const s = (transcript ?? '')
    .replace(/['’‘`]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9./]+/g, ' ')
    .replace(/[./]+(?=\s|$)/g, ' ')
    .replace(/(^|\s)[./]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return s === '' ? '' : ' ' + s + ' ';
}

function containsAny(padded: string, tokens: readonly string[]): boolean {
  if (padded === '') return false;
  for (const tok of tokens) if (padded.includes(' ' + tok + ' ')) return true;
  return false;
}

export interface BatchPublishAsk {
  count: number;
  /** what the user called the site ("booksesh.com", "booksesh", "partners"), or null. */
  siteHint: string | null;
  /** the mode the user already named in the ask ("publish the next 3"), or null. */
  namedMode: BatchMode | null;
  /** F3 (r2 owner device test 10-10-2026 21:25:22): the draft TITLE the user named
   *  ("can you push understanding sound healing session?"), or null/absent. */
  titleHint?: string | null;
  /** Re-push guard: "update the live post ..." -- an explicit request to rewrite a LIVE post. */
  updateLive?: boolean;
  /** conversational-actions (owner GO 12-10-2026 20:30 UTC): "write <title> that is on the
   *  queue" -- the draft is already written, so Aura says so and offers to publish it. */
  alreadyWritten?: boolean;
}

/** Verbs that can open the batch flow. "push"/"publish"/"release" NAME publish and
 *  "save"/"draft" NAME draft (owner 10-10-2026: "user say publish/push it"; owner
 *  report 18:48 UTC: Aura "didnt take my clear instruction"). Others ask. */
const ASK_VERBS = ['push', 'publish', 'post', 'send', 'put', 'upload', 'release', 'save', 'draft'];
const PUBLISH_ASK_VERBS = ['push', 'publish', 'release'];
const DRAFT_ASK_VERBS = ['save', 'draft'];
const ASK_NOUNS = ['drafts', 'draft', 'posts', 'post', 'blogs', 'blog', 'articles', 'article', 'blog posts'];
/** Trailing words after the site that are not part of the site's name. */
const SITE_TAIL_RE = / (?:as (?:a )?drafts?|please|right now|now|for me|straight away|immediately|live|then|thanks|thank you) $/;
const SITE_FILLER = new Set(['my', 'the', 'it', 'wordpress', 'website', 'site', 'blog', 'our', 'a']);

/** The verb the user LED with (earliest in the sentence), e.g. "push the next draft" -> push. */
function leadingVerb(p: string): string | null {
  let best: string | null = null;
  let at = Infinity;
  for (const v of ASK_VERBS) {
    const i = p.indexOf(' ' + v + ' ');
    if (i >= 0 && i < at) { at = i; best = v; }
  }
  return best;
}

/** What the user called the site, from the words after to/on/onto/for/at that follow
 *  the batch cue. Spoken forms are kept tolerant: "book sesh dot com" -> "book sesh.com". */
function extractSiteHint(p: string, from: number): string | null {
  const tail = ' ' + p.slice(Math.max(0, from)).trim() + ' ';
  const m = tail.match(/ (?:to|on|onto|for|at) (.+?) $/);
  if (!m) return null;
  let h = ' ' + m[1] + ' ';
  for (let i = 0; i < 4 && SITE_TAIL_RE.test(h); i += 1) h = h.replace(SITE_TAIL_RE, ' ');
  h = h
    .replace(/ dot /g, '.')
    .replace(/ (?:forward )?slash /g, '/')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^(?:the )?(?:site )?/, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '')
    .trim();
  if (h === '' || SITE_FILLER.has(h)) return null;
  return h;
}

/**
 * Detect "push / publish the next N drafts (to <site>)". Deterministic, closed
 * set, OFF the model. Returns null for anything else (the normal Aura path runs).
 * Requires a verb AND a content noun AND a batch cue (next / a number / these /
 * my) so ordinary questions ("how do I publish a post?") are not hijacked.
 */
export function detectBatchPublishAsk(transcript: string): BatchPublishAsk | null {
  const p = normAsk(transcript);
  if (p === '') return null;
  const upd = detectUpdateLiveAsk(p);
  if (upd) return upd;
  // conversational-actions (owner GO 12-10-2026 20:30 UTC; diagnosis F1, owner session
  // 20:16:04 "Can you push why wellness practitioners struggle with bookings?"): a question
  // word INSIDE a named title must not block the titled publish. Only when filler alone
  // precedes the publish verb (detectTitledPublishAsk's own rule); "why should I push…" and
  // "how do I publish a post?" stay questions.
  if (/\b(how|what|why|when|should i|can i|do i)\b/.test(p) && !/\b(next|these|them)\b/.test(p)) {
    if (/ (dont|do not|never|not) /.test(p) || / (should i|shall i|can i|could i|do i|would it|is it|should we|shall we) /.test(p)) return null;
    return detectTitledPublishAsk(p);
  }
  // A-AJ2 (Fable IVG 10-10-2026): a negation or a question about publishing is never
  // an instruction ("don't push the next draft…", "should I push the next draft…?").
  if (/ (dont|do not|never|not) /.test(p)) return null;
  if (/ (should i|shall i|can i|could i|do i|would it|is it|should we|shall we) /.test(p)) return null;
  const verb = leadingVerb(p);
  if (!verb) return null;
  // "push the one on the queue" / "publish the queued one": the next ready draft.
  const q = stripQueueRef(p);
  if (q.queue && PUBLISH_ASK_VERBS.includes(verb)) {
    const titled = detectTitledPublishAsk(q.rest);
    if (titled) return titled;
    return { count: 1, siteHint: null, namedMode: 'publish' };
  }
  const noun = ASK_NOUNS.some((n) => p.includes(' ' + n + ' '));
  if (!noun) return detectTitledPublishAsk(p);
  const m = p.match(/ (next|the next|my next|these|my|the) ?(\d+|one|two|three|four|five|couple|few)? ?(?:ready |pending |approved |blog )?(drafts?|posts?|blogs?|articles?) /);
  const digit = p.match(/ (\d+) (?:ready |pending |approved |blog )?(?:drafts?|posts?|blogs?|articles?) /);
  // A-AJ5: "save the next 3 as drafts on <site>" (count, then "as drafts").
  const asDrafts = !m && !digit ? p.match(/ (?:next|the next|my next) (\d+|one|two|three|four|five|couple|few) as (?:a )?drafts? /) : null;
  if (!m && !digit && !asDrafts) return null;
  let count = BATCH_DEFAULT_COUNT;
  const raw = (digit?.[1] ?? m?.[2] ?? asDrafts?.[1] ?? '').trim();
  if (raw !== '') count = /^\d+$/.test(raw) ? parseInt(raw, 10) : NUMBER_WORDS[raw] ?? BATCH_DEFAULT_COUNT;
  else if (m && /^drafts?$|^posts?$|^blogs?$|^articles?$/.test(m[3]) && !/s$/.test(m[3])) count = 1; // "the next draft" = one
  if (!Number.isFinite(count) || count < 1) count = 1;
  if (count > BATCH_MAX_COUNT) count = BATCH_MAX_COUNT;
  const cueEnd = Math.max(
    m && m.index !== undefined ? m.index + m[0].length - 1 : -1,
    digit && digit.index !== undefined ? digit.index + digit[0].length - 1 : -1,
    asDrafts && asDrafts.index !== undefined ? asDrafts.index + asDrafts[0].length - 1 : -1,
  );
  const siteHint = extractSiteHint(p, cueEnd);
  // Mode the user NAMED. The safer draft wins a mixed sentence ("publish ... as drafts").
  const namedMode: BatchMode | null =
    containsAny(p, ['as drafts', 'as draft', 'as a draft']) || DRAFT_ASK_VERBS.includes(verb)
      ? 'draft'
      : PUBLISH_ASK_VERBS.includes(verb) || containsAny(p, ['go live', 'make it live', 'make them live'])
        ? 'publish'
        : null;
  return { count, siteHint, namedMode };
}

/** Queue references: "the one on the queue", "that is on the queue", "the queued one", "the next one". */
const QUEUE_RE = / (?:(?:that is |thats |that s |which is |thats in |its )?(?:on|in) (?:the |my )?queue|(?:the )?queued(?: one| blog| draft| post| article)?|(?:the )?next one|already written) /;
export function stripQueueRef(padded: string): { queue: boolean; rest: string } {
  if (!QUEUE_RE.test(padded)) return { queue: false, rest: padded };
  return { queue: true, rest: padded.replace(QUEUE_RE, ' ').replace(/\s+/g, ' ') };
}

/** Words that never make a draft title on their own ("push it", "push them live"). */
const TITLE_STOP = new Set(['it', 'them', 'that', 'this', 'those', 'these', 'one', 'live', 'now', 'please', 'up', 'out', 'through', 'the', 'a', 'my', 'all', 'everything', 'button', 'notification', 'notifications']);
const TITLE_LEAD_RE = /^(?:the |my |our |a |an )+/;
const TITLE_TAIL_RE = / (?:live|please|now|right now|for me|straight away|immediately|as well|too|thanks|thank you)$/;

/**
 * F3 (owner device test 10-10-2026 21:25:22: "Can you push understanding sound
 * healing session?" routed conversational): a PUBLISH verb (push / publish /
 * release) followed by a named draft TITLE -- no "draft" word, no site needed.
 * The site is the viewed domain (resolveBatchSite with a null hint); the title is
 * fuzzy-matched against the ready drafts (matchDraftTitle) and ONE read-back
 * follows. Needs >= 2 meaningful title words so "push it" / "push them live" /
 * "push notifications" never start a publish. Pure.
 */
function detectTitledPublishAsk(p: string): BatchPublishAsk | null {
  let at = Infinity;
  let verb: string | null = null;
  for (const v of PUBLISH_ASK_VERBS) {
    const i = p.indexOf(' ' + v + ' ');
    if (i >= 0 && i < at) { at = i; verb = v; }
  }
  if (!verb) return null;
  // only filler may precede the verb ("can you", "could you please", "aura", "please", "now")
  const before = p.slice(0, at).trim();
  if (before !== '' && !/^(?:(?:hey )?aura )?(?:(?:can|could|would|will) you )?(?:please )?(?:go ahead and )?(?:now )?$/.test(before + ' ')) return null;
  let rest = p.slice(at + verb.length + 2).trim();
  // Site: ONLY the LAST to/on/onto/at clause, and only when it looks like a site
  // ("booksesh.com", "book sesh dot com", "the partners site") -- titles carry
  // "for"/"to" too ("reiki for beginners", "what to expect from kambo").
  let siteHint: string | null = null;
  const sm = rest.match(/^(.*) (?:to|on|onto|at) (\S+(?: \S+){0,4})$/);
  if (sm) {
    const cand = extractSiteHint(' to ' + sm[2] + ' ', 0);
    if (cand && (cand.includes('.') || /\b(?:partners|site)\b/.test(sm[2]))) { siteHint = cand; rest = sm[1].trim(); }
  }
  for (let i = 0; i < 3 && TITLE_TAIL_RE.test(rest); i += 1) rest = rest.replace(TITLE_TAIL_RE, '').trim();
  rest = rest.replace(TITLE_LEAD_RE, '').trim();
  const meaningful = rest.split(' ').filter((w) => w !== '' && !TITLE_STOP.has(w));
  if (meaningful.length < 2) return null;
  return { count: 1, siteHint, namedMode: 'publish', titleHint: rest };
}

const titleTokens = (s: string): string[] =>
  s.toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/).filter((w) => w.length > 1 && !TITLE_STOP.has(w) && !['and', 'of', 'in', 'to', 'for', 'what', 'how', 'your', 'you', 'is', 'are', 'with', 'on',
    // spoken filler around a title (owner session 20:11:58: "...why wellness booking, etcetera, etcetera...")
    'etcetera', 'etc', 'regarding', 'called', 'titled', 'named', 'blah'].includes(w)).map((w) => w.replace(/(?:ing|s)$/, ''));

/** F3: best ready draft for a spoken title. Score = share of the spoken title's
 *  words found in the draft title (prefix/stem tolerant); needs >= 0.6 and >= 2
 *  shared words; a tie keeps the server order (next-in-queue first). Pure. */
export function matchDraftTitle<T extends { title: string }>(hint: string, items: readonly T[]): { item: T; score: number } | null {
  const h = Array.from(new Set(titleTokens(hint)));
  if (h.length === 0) return null;
  let best: { item: T; score: number; shared: number } | null = null;
  for (const it of items) {
    const t = new Set(titleTokens(it.title));
    let shared = 0;
    for (const w of h) if (t.has(w) || Array.from(t).some((x) => x.length >= 4 && w.length >= 4 && (x.startsWith(w) || w.startsWith(x)))) shared += 1;
    const score = shared / h.length;
    if (!best || score > best.score) best = { item: it, score, shared };
  }
  if (!best || best.score < 0.6 || best.shared < Math.min(2, h.length)) return null;
  return { item: best.item, score: best.score };
}

/** F3: spoken when no ready draft matches the named title. */
export function titleNotFoundLine(hint: string, siteName: string): string {
  return `I couldn't find a ready draft called "${hint}" for ${siteName}.`;
}

/**
 * Re-push guard (owner decision 12-10-2026 15:29 UTC, verbatim: "the guard was to check if a
 * previously pushed blog already doesnt get repushed/written unless it's intentional"; "yes add
 * the guard"): the explicit, intentional path is "update the live post (<title>) (on <site>)",
 * or -- right after Aura said a draft is already live -- "update it on the live site".
 * Connector 1.3.0 (owner GO 12-10-2026 15:53 UTC, option A: "Add a proper 'update live post'
 * action to the connector, with a saved copy for undo ... the post stays live throughout")
 * makes this an executable, separately-confirmed action: a server read-back naming the live
 * post, then ONLY a pure yes or a tap. Everything else is unchanged. Pure.
 */
function detectUpdateLiveAsk(p: string): BatchPublishAsk | null {
  if (/ (dont|do not|never|not) /.test(p) || / (should i|shall i|can i|could i) /.test(p)) return null;
  // "update it on the live site" / "update it live" / "update that one on the live site":
  // refers to the draft Aura just said is already live (no title in the phrase).
  if (/ (?:update|refresh) (?:it|that|this)(?: one| post| blog)? (?:on|to|in) (?:the )?live (?:site|website|post|version|one) $/.test(p)
    || / (?:update|refresh) (?:it|that|this)(?: one)? live $/.test(p)) {
    return { count: 1, siteHint: null, namedMode: 'publish', titleHint: null, updateLive: true };
  }
  const m = p.match(/ (?:update|rewrite|overwrite|replace|edit|change|refresh) (?:the |my |our )?(?:live|published) (?:post|blog|article|page|one)(?: called| named| titled)?(.*)$/);
  if (!m) return null;
  let rest = (m[1] ?? '').trim();
  let siteHint: string | null = null;
  // Site: only a LAST on/to/at clause that looks like a site (titles carry "on"/"to" too).
  const sm = rest.match(/^(.*) (?:on|to|at) (\S+(?: \S+){0,4})$/);
  if (sm) {
    const cand = extractSiteHint(' to ' + sm[2] + ' ', 0);
    if (cand && (cand.includes('.') || /\b(?:partners|site)\b/.test(sm[2]))) { siteHint = cand; rest = sm[1].trim(); }
  }
  rest = rest.replace(TITLE_LEAD_RE, '').trim();
  return { count: 1, siteHint, namedMode: 'publish', titleHint: rest !== '' ? rest : null, updateLive: true };
}

/** Spoken when this build has no update-live wiring (deps absent): nothing changes. */
export const BATCH_UPDATE_LIVE_UNSUPPORTED =
  'I can’t update a live post yet, so nothing was changed. Edit it in WordPress, or undo the publish first.';

/** Connector 1.3.0: the server refuses an old plugin with exactly this line (BatchPublishService). */
export const BATCH_CONNECTOR_UPDATE_REQUIRED = 'Update the connector to use this.';
export const BATCH_CONNECTOR_UPDATE_REQUIRED_CODE = 'connector_update_required';
/** "update it on the live site" with no live post in context. */
export const BATCH_UPDATE_LIVE_WHICH = 'Which live post? Say “update the live post” and its title, or tap Update live post.';
/** Only a pure yes (or a tap) confirms the update read-back; anything else gets this once. */
export const BATCH_UPDATE_LIVE_REASK = 'Say yes to update the live post, or no to leave it.';
export function updateLiveNotFoundLine(hint: string, siteName: string): string {
  return `I couldn't find a live post called "${hint}" on ${siteName}.`;
}
/** The server read-back, mirrored for tests/fallback: "Update the live post '<title>' on <site>?" */
export function updateLiveReadbackLine(title: string, siteName: string): string {
  return `Update the live post '${(title ?? '').replace(/'/g, '’')}' on ${siteName}?`;
}

/** Spoken when a draft named for a normal push is already live on that site (never re-pushed). */
export function alreadyLiveLine(title: string, siteName: string): string {
  return `‘${speakableTitle(title)}’ is already live on ${siteName}, so I won’t push it again.`;
}

/** Card / list labels (owner decision 12-10-2026): already pushed to THAT site. */
export function alreadyLiveLabel(siteName: string): string { return `Already live on ${siteName}`; }
export function alreadyDraftLabel(siteName: string): string { return `Already on ${siteName} as a draft`; }

export type BatchAnswer =
  | { kind: 'publish' }
  | { kind: 'draft' }
  | { kind: 'bare_yes' }
  | { kind: 'no' }
  | { kind: 'unrecognised' };

const NO_TOKENS = ['no', 'nope', 'nah', 'not', 'never', 'cancel', 'dont', 'do not', 'not now', 'not yet', 'leave it', 'leave them', 'hold on', 'hold off', 'wait', 'later', 'stop'];
const DRAFT_TOKENS = ['draft', 'drafts', 'as draft', 'as drafts', 'save as draft', 'save them as drafts', 'save', 'keep as draft', 'keep them as drafts', 'just draft'];
const PUBLISH_TOKENS = ['publish', 'publish it', 'publish them', 'publish all', 'push it', 'push them', 'push', 'go live', 'make it live', 'make them live', 'put it live', 'put them live', 'post it', 'post them'];
const YES_TOKENS = ['yes', 'yeah', 'yep', 'yup', 'sure', 'ok', 'okay', 'go ahead', 'do it', 'please do', 'confirm', 'go for it', 'yes please', 'sounds good'];
const HEDGE_TOKENS = ['maybe', 'perhaps', 'i guess', 'unsure', 'hmm', 'um', 'dunno', 'dont know', 'what', 'which', 'why', 'how'];

/** Match a spoken/typed answer to "Shall I push the next N drafts to <site>?". */
export function matchBatchPublishAnswer(transcript: string): BatchAnswer {
  const p = norm(transcript);
  if (p === '') return { kind: 'unrecognised' };
  if (isTurnClose(transcript) || containsAny(p, NO_TOKENS)) return { kind: 'no' };
  if (containsAny(p, HEDGE_TOKENS)) return { kind: 'unrecognised' };
  if (containsAny(p, DRAFT_TOKENS)) return { kind: 'draft' }; // safer of the two wins a mixed answer
  if (containsAny(p, PUBLISH_TOKENS)) return { kind: 'publish' };
  if (containsAny(p, YES_TOKENS)) return isPureAffirmative(p) ? { kind: 'bare_yes' } : { kind: 'unrecognised' };
  return { kind: 'unrecognised' };
}

/** C-AJ1 (Fable IVG 10-10-2026, REQUIRED): only a PURE affirmative may confirm a
 *  read-back. Every word must be an affirmative or a harmless filler, at most
 *  PURE_YES_MAX_WORDS words. Anything else (another site, a time, a condition, a
 *  second request, a name: "yes but to partners", "yes tomorrow", "yes mum") is NOT
 *  a confirmation and gets the short re-ask. */
const PURE_YES_WORDS = new Set([
  'yes', 'yeah', 'yep', 'yup', 'sure', 'ok', 'okay', 'confirm', 'confirmed', 'go', 'ahead', 'do', 'it',
  'for', 'sounds', 'good', 'alright', 'please', 'aura', 'thanks', 'thank', 'you', 'then', 'now', 'right',
]);
export const PURE_YES_MAX_WORDS = 4;
export function isPureAffirmative(padded: string): boolean {
  const words = padded.trim().split(' ').filter(Boolean);
  return words.length > 0 && words.length <= PURE_YES_MAX_WORDS && words.every((w) => PURE_YES_WORDS.has(w));
}

/** What a confirmed read-back may carry: a batch mode, or the separate connector-1.3.0 update. */
export type BatchReadbackMode = BatchMode | 'update_live';

export type BatchStep =
  | { kind: 'execute'; mode: BatchMode }
  | { kind: 'execute-update-live' }
  | { kind: 'ask-mode' }
  | { kind: 're-ask' }
  | { kind: 'cancel' };

/**
 * A bare yes ("yes" / "confirm" / "go ahead") executes ONLY when the spoken
 * read-back already named the action, the site and the count (readbackMode set).
 * Otherwise it asks "Publish or save as draft?" (counts as the one re-ask).
 */
export function resolveBatchPublishAnswer(match: BatchAnswer, reAskCount: number, readbackMode: BatchReadbackMode | null = null): BatchStep {
  if (readbackMode === 'update_live') {
    // Connector 1.3.0 update: ONLY a pure yes confirms ("publish"/"push it"/"draft" never do).
    if (match.kind === 'bare_yes') return { kind: 'execute-update-live' };
    if (match.kind === 'no') return { kind: 'cancel' };
    return reAskCount >= BATCH_PUBLISH_MAX_REASKS ? { kind: 'cancel' } : { kind: 're-ask' };
  }
  if (match.kind === 'publish') return { kind: 'execute', mode: 'publish' };
  if (match.kind === 'draft') return { kind: 'execute', mode: 'draft' };
  if (match.kind === 'no') return { kind: 'cancel' };
  if (match.kind === 'bare_yes' && readbackMode) return { kind: 'execute', mode: readbackMode };
  if (reAskCount >= BATCH_PUBLISH_MAX_REASKS) return { kind: 'cancel' };
  return match.kind === 'bare_yes' ? { kind: 'ask-mode' } : { kind: 're-ask' };
}

/**
 * Server reason (PublishPolicy::ROLE_NOT_PERMITTED) when the signed-in human's role may
 * not publish — owner decision C6, 10-10-2026, "owners and operators only"
 * (docs/brain/governance/owner-decision-aura-publish-choice-2026-10-10.md). The server
 * is the authority (it downgrades to draft); the UI only mirrors it.
 */
export const BATCH_PUBLISH_ROLE_REASON = 'publish_requires_owner_or_operator';

/**
 * C6b — the WHOLE batch-publish feature is owner/operator only (owner decision
 * 10-10-2026, verbatim "obviously - none other than owner or operator should have
 * access to it or publish anything"; decision file last section, recorded 16:33 UTC).
 * The server refuses every batch route for any other role with 403
 * BATCH_FEATURE_FORBIDDEN_CODE; the UI mirrors it: no cards, no buttons, no voice
 * flow — Aura only says BATCH_OWNER_OPERATOR_ONLY. The server remains the authority.
 */
export const BATCH_FEATURE_FORBIDDEN_CODE = 'publish_feature_owner_operator_only';
export const BATCH_OWNER_OPERATOR_ONLY = 'Only workspace owners and operators can manage publishing.';

/** C6b: may this workspace role use the batch-publish feature at all? Exact match, fail-closed. */
export function canManagePublishing(role: string | null | undefined): boolean {
  return role === 'owner' || role === 'operator';
}

/** C6b: is this error the server's owner/operator-only refusal? (ApiError.code or an {body:{error:{code}}} shape) */
export function isPublishFeatureForbidden(e: unknown): boolean {
  const x = e as { code?: unknown; body?: { error?: { code?: unknown } } } | null | undefined;
  return x?.code === BATCH_FEATURE_FORBIDDEN_CODE || x?.body?.error?.code === BATCH_FEATURE_FORBIDDEN_CODE;
}

/**
 * No action named yet ("send the next 3 drafts to booksesh.com"): ONE sentence,
 * the cards carry the detail. "Push" is NOT used here: for this owner "push" means
 * publish (owner 10-10-2026), so the question names both options plainly.
 */
export function batchProposalLine(count: number, siteName: string, publishAvailable: boolean, publishReason = ''): string {
  const n = Math.max(1, Math.floor(count));
  const what = n === 1 ? 'The next draft' : `The next ${n} drafts`;
  const are = n === 1 ? 'is' : 'are';
  if (publishAvailable) return `${what} for ${siteName} ${are} on screen — publish or save as draft?`;
  if (publishReason === BATCH_PUBLISH_ROLE_REASON) {
    return `${what} for ${siteName} ${are} on screen — only workspace owners and operators can publish, so say draft or tap Draft.`;
  }
  return `${what} for ${siteName} ${are} on screen — this site is drafts only, so say draft or tap Draft.`;
}

/** conversational-actions: the question after the user narrowed the cards ("2", "1 and 3")
 *  without naming an action. */
export function batchSubsetQuestion(items: readonly { title: string }[]): string {
  if (items.length === 1) return `‘${speakableTitle(items[0].title)}’ — publish or save as draft?`;
  return `Those ${items.length} — publish or save as draft?`;
}

/** Speakable title: straight quotes stripped, long titles cut at a word boundary. */
export function speakableTitle(title: string, max = 80): string {
  const t = (title ?? '').replace(/[‘’'"“”]/g, '').replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return (sp > 30 ? cut.slice(0, sp) : cut).replace(/[\s,;:–—-]+$/, '') + '…';
}

/**
 * The action AND the site were both clear in the command: ONE short read-back that
 * names the action, the site and the count (a single title stands for "one"). A
 * bare "yes" / "confirm" / "go ahead" / a tap then confirms exactly this.
 * Returns the line plus the mode it reads back. When the site cannot publish
 * (drafts-only / WP opt-in off / kill switch, as reported by the server), the
 * read-back says so and reads back DRAFT — it never promises a publish.
 */
export function batchReadbackLine(
  mode: BatchMode,
  items: readonly { title: string; state?: string }[],
  siteName: string,
  publishAvailable: boolean,
): { line: string; mode: BatchMode } {
  const n = Math.max(1, items.length);
  const one = n === 1 ? speakableTitle(items[0]?.title ?? '') : '';
  // Re-push guard (owner decision 12-10-2026): a card already on the site as a WP draft is
  // named in the read-back, so the yes is an informed "update it".
  const onSite = items.filter((i) => i.state === 'on_site_as_draft').length;
  if (onSite > 0) {
    if (n === 1) {
      const t = one !== '' ? `‘${one}’` : 'it';
      if (mode === 'publish' && publishAvailable) return { line: `${t} is already on ${siteName} as a draft — update it and publish it?`, mode: 'publish' };
      if (mode === 'publish') return { line: `Publishing is off for ${siteName}; ${t} is already there as a draft — update the draft?`, mode: 'draft' };
      return { line: `Already pushed as a draft — update ${t} on ${siteName}?`, mode: 'draft' };
    }
    const already = onSite === 1 ? '1 is already there as a draft and will be updated' : `${onSite} are already there as drafts and will be updated`;
    if (mode === 'publish' && publishAvailable) return { line: `Publish ${n} posts to ${siteName}? ${already}.`, mode: 'publish' };
    if (mode === 'publish') return { line: `Publishing is off for ${siteName}, so save the ${n} posts as drafts instead? ${already}.`, mode: 'draft' };
    return { line: `Save ${n} posts as drafts on ${siteName}? ${already}.`, mode: 'draft' };
  }
  const subject = one !== '' ? `‘${one}’` : n === 1 ? 'the next draft' : `${n} posts`;
  if (mode === 'publish' && publishAvailable) return { line: `Publish ${subject} to ${siteName}?`, mode: 'publish' };
  if (mode === 'publish') {
    const draftSubject = one !== '' ? `‘${one}’ as a draft` : n === 1 ? 'it as a draft' : `the ${n} posts as drafts`;
    return { line: `Publishing is off for ${siteName}, so save ${draftSubject} instead?`, mode: 'draft' };
  }
  const asDraft = one !== '' ? `‘${one}’ as a draft` : n === 1 ? 'the next draft as a draft' : `${n} posts as drafts`;
  return { line: `Save ${asDraft} on ${siteName}?`, mode: 'draft' };
}

// ---- site resolution (pure) -------------------------------------------------

export interface BatchSiteOption {
  id: number;
  label: string;
  url: string;
  name: string; // host + path, no www (matches the server's site.name)
  projectId: number | null;
}

export function siteNameOf(url: string, label: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/+$/, '');
    return u.hostname.replace(/^www\./i, '') + path;
  } catch {
    return label || url;
  }
}

/** Active, draft-pushable connections -> site options. */
export function batchSiteOptions(
  connections: { id: number; site_label?: string; site_url?: string; status?: string; capabilities?: Record<string, unknown> | null; project_id?: number | null }[],
): BatchSiteOption[] {
  return (connections ?? [])
    .filter((c) => c && Number(c.id) > 0 && (c.status ?? 'active') === 'active' && c.capabilities != null && Boolean((c.capabilities as Record<string, unknown>).push_drafts))
    .map((c) => {
      const url = (c.site_url ?? '').trim();
      const label = (c.site_label ?? '').trim() || url || 'Site #' + c.id;
      return { id: Number(c.id), label, url, name: siteNameOf(url, label), projectId: c.project_id ?? null };
    });
}

export type SiteResolution =
  | { kind: 'one'; site: BatchSiteOption }
  | { kind: 'choose'; options: BatchSiteOption[] }
  | { kind: 'out_of_domain' }
  | { kind: 'none' };

/** The domain shown in the header ("Viewing: booksesh.com"). projectId is the
 *  server's active SEO domain; hostname is its host (no scheme, no www). */
export interface BatchActiveDomain {
  projectId: number | null;
  hostname: string;
}

/** Minimum similarity for a spoken site stem to count as a match (calibrated on
 *  real Deepgram outputs: "booksteps" / "booksearch" / "book sesh" -> booksesh). */
export const SITE_MATCH_THRESHOLD = 0.72;

const TLD_TOKENS = new Set(['com', 'co', 'uk', 'net', 'org', 'io', 'ai', 'app', 'dev', 'biz', 'info', 'me']);
const ROOT_WORDS = new Set(['main', 'root', 'home', 'homepage', 'primary']);
const HINT_FILLER = new Set(['the', 'site', 'website', 'my', 'our', 'wordpress', 'blog', 'page', 'to', 'on', 'at', 'for', 'one', 'www', 'domain', 'please']);

function hostOfName(name: string): string {
  const n = name.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
  const i = n.indexOf('/');
  return i < 0 ? n : n.slice(0, i);
}
function pathOfName(name: string): string {
  const n = name.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
  const i = n.indexOf('/');
  return i < 0 ? '' : n.slice(i + 1).replace(/\/+$/, '');
}
/** "booksesh.com" -> "booksesh"; "omegahub.co.uk" -> "omegahub"; "pro-web.designs.com" -> "prowebdesigns". */
export function hostStem(host: string): string {
  const parts = host.toLowerCase().replace(/^www\./, '').split('.').filter(Boolean);
  while (parts.length > 1 && TLD_TOKENS.has(parts[parts.length - 1])) parts.pop();
  return parts.join('').replace(/[^a-z0-9]/g, '');
}
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const cur = [i];
    for (let j = 1; j <= b.length; j += 1) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
}
function jaroWinkler(a: string, b: string): number {
  if (a === b) return 1;
  if (!a || !b) return 0;
  const win = Math.max(0, Math.floor(Math.max(a.length, b.length) / 2) - 1);
  const am = new Array(a.length).fill(false);
  const bm = new Array(b.length).fill(false);
  let matches = 0;
  for (let i = 0; i < a.length; i += 1) {
    for (let j = Math.max(0, i - win); j < Math.min(b.length, i + win + 1); j += 1) {
      if (!bm[j] && a[i] === b[j]) { am[i] = true; bm[j] = true; matches += 1; break; }
    }
  }
  if (matches === 0) return 0;
  let k = 0; let t = 0;
  for (let i = 0; i < a.length; i += 1) {
    if (!am[i]) continue;
    while (!bm[k]) k += 1;
    if (a[i] !== b[k]) t += 1;
    k += 1;
  }
  const jaro = (matches / a.length + matches / b.length + (matches - t / 2) / matches) / 3;
  let pre = 0;
  while (pre < 4 && a[pre] === b[pre]) pre += 1;
  return jaro + pre * 0.1 * (1 - jaro);
}
/** Similarity of two compact stems, 0..1: the mean of edit-distance and Jaro-Winkler
 *  similarity, so a shared brand prefix helps but a long unrelated name does not. */
export function stemSimilarity(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const lev = 1 - levenshtein(a, b) / Math.max(a.length, b.length);
  return (lev + jaroWinkler(a, b)) / 2;
}

export interface ParsedSiteHint {
  /** compact host stem the user said ("booksesh"), '' when they named no host. */
  stem: string;
  /** a path the user named ("partners"), matched against the known site paths, or ''. */
  path: string;
  /** "the main site" / "root" / "home". */
  wantRoot: boolean;
  /** the user said a domain ending ("dot com", ".co.uk") — i.e. named a specific domain. */
  namedDomain: boolean;
}

function tokenMatchesPath(tok: string, path: string): boolean {
  if (!tok || !path) return false;
  const p = path.replace(/[^a-z0-9]/g, '');
  if (tok === p) return true;
  if (tok.length >= 5 && p.startsWith(tok.slice(0, 5)) && Math.abs(tok.length - p.length) <= 2) return true;
  return stemSimilarity(tok, p) >= 0.8;
}

/** Parse a spoken/typed site hint tolerantly: dots as "dot", spaces inside the
 *  brand ("book sesh", "books esh"), "slash partners", "the partners site". */
export function parseSiteHint(hint: string, knownPaths: readonly string[]): ParsedSiteHint {
  const raw = (hint ?? '')
    .toLowerCase()
    .replace(/https?:\/\//g, ' ')
    .replace(/\bdot\b/g, '.')
    .replace(/\b(?:forward )?slash\b/g, '/')
    .replace(/[^a-z0-9./ -]+/g, ' ');
  const tokens = raw.split(/[\s./-]+/).filter(Boolean);
  let path = '';
  let wantRoot = false;
  let namedDomain = false;
  const stemParts: string[] = [];
  for (const tok of tokens) {
    if (ROOT_WORDS.has(tok)) { wantRoot = true; continue; }
    if (TLD_TOKENS.has(tok)) { if (stemParts.length > 0) namedDomain = true; continue; }
    if (HINT_FILLER.has(tok)) continue;
    const kp = knownPaths.find((p) => tokenMatchesPath(tok, p));
    if (kp) { path = kp; continue; }
    stemParts.push(tok);
  }
  return { stem: stemParts.join(''), path, wantRoot, namedDomain };
}

/** Sites under the active domain: the same SEO project, or the same host. */
export function sitesInActiveDomain(options: BatchSiteOption[], active: BatchActiveDomain | null): BatchSiteOption[] {
  if (!active) return [];
  const host = hostOfName(active.hostname || '');
  return options.filter((o) => (active.projectId != null && o.projectId === active.projectId) || (host !== '' && hostOfName(o.name) === host));
}

/** Within one domain's sites: the root (no path) unless a path / root word says otherwise. */
function pickWithinDomain(sites: BatchSiteOption[], parsed: ParsedSiteHint): SiteResolution {
  if (sites.length === 0) return { kind: 'none' };
  if (sites.length === 1) {
    // A path the user named must exist; otherwise the one site is the answer.
    if (parsed.path && pathOfName(sites[0].name) !== parsed.path) return { kind: 'choose', options: sites };
    return { kind: 'one', site: sites[0] };
  }
  if (parsed.path) {
    const byPath = sites.filter((s) => pathOfName(s.name) === parsed.path);
    if (byPath.length === 1) return { kind: 'one', site: byPath[0] };
    return { kind: 'choose', options: byPath.length > 1 ? byPath : sites };
  }
  // A plain domain name, "the main site", or no hint at all -> the root target.
  const roots = sites.filter((s) => pathOfName(s.name) === '');
  if (roots.length === 1) return { kind: 'one', site: roots[0] };
  return { kind: 'choose', options: roots.length > 1 ? roots : sites };
}

/**
 * Pick the site, scoped to the domain the user is VIEWING (owner 10-10-2026 18:48
 * UTC: "why would you give me choice of domains that's not part of this domain i'm
 * working with in the workspace").
 *   - Candidates come ONLY from the active domain's sites; other workspace domains
 *     never appear as options.
 *   - A site named in the command is matched tolerantly (STT variants). If it is
 *     better matched by a site OUTSIDE the active domain -> out_of_domain (Aura
 *     says so; never a silent switch). If it matches nothing -> choose among the
 *     active domain's sites only.
 *   - booksesh.com root + /partners: a plain "booksesh.com", "the main site" or no
 *     hint -> the root; "partners" -> /partners; ask only if still ambiguous.
 *   - No active domain (the "All domains" lens / not loaded): the same tolerant
 *     matching across all sites; a non-match chooses among all.
 * Accepts a bare project id (legacy callers) as the active domain.
 */
/**
 * r2 domain scope, Bug A (owner live test 12-10-2026 15:11:25 UTC): the wake
 * remainder ("push the next draft to booksesh.com") is merged with the capture,
 * which repeats the site ("To booksearch.com."), so the hint arrives as
 * "booksesh.com to booksearch.com". As one phrase its stem ("bookseshbooksearch")
 * matched nothing and the named-domain rule refused it ("That site isn't in the
 * domain you're viewing"). A hint is therefore split into its site phrases and
 * each is resolved on its own:
 *   - a phrase that clearly names ANOTHER workspace domain ("… and omegahub.co.uk")
 *     is a conflicting request -> out_of_domain (never silently pick one);
 *   - otherwise any phrase resolving to ONE site wins (a phrase naming a path, e.g.
 *     "/partners", beats a plain domain; otherwise the first);
 *   - else any phrase asking to choose -> choose (viewed domain's sites only);
 *   - else (every phrase out of the domain) -> out_of_domain.
 */
export function splitSiteHint(hint: string): string[] {
  return (hint ?? '')
    .split(/\s+(?:to|on|onto|at|and|or|then)\s+/i)
    .map((x) => x.trim())
    .filter((x) => x !== '');
}

export function resolveBatchSite(
  options: BatchSiteOption[],
  hint: string | null,
  activeIn: BatchActiveDomain | number | null,
): SiteResolution {
  const parts = hint ? splitSiteHint(hint) : [];
  const plain = (r: SiteResolution): SiteResolution => (r.kind === 'out_of_domain' ? { kind: 'out_of_domain' } : r);
  if (parts.length <= 1) return plain(resolveOneSiteHint(options, parts[0] ?? hint, activeIn));
  const results = parts.map((p) => resolveOneSiteHint(options, p, activeIn));
  if (results.some((r) => r.kind === 'out_of_domain' && (r as { otherDomain?: boolean }).otherDomain === true)) {
    return { kind: 'out_of_domain' };
  }
  const ones = results.filter((r): r is { kind: 'one'; site: BatchSiteOption } => r.kind === 'one');
  if (ones.length > 0) {
    const withPath = ones.find((r) => pathOfName(r.site.name) !== '' && parts.some((p) => /partner|\//i.test(p)));
    return withPath ?? ones[0];
  }
  const choose = results.find((r) => r.kind === 'choose');
  if (choose) return choose;
  if (results.some((r) => r.kind === 'out_of_domain')) return { kind: 'out_of_domain' };
  return plain(results[0]);
}

function resolveOneSiteHint(
  options: BatchSiteOption[],
  hint: string | null,
  activeIn: BatchActiveDomain | number | null,
): SiteResolution {
  if (options.length === 0) return { kind: 'none' };
  const active: BatchActiveDomain | null =
    typeof activeIn === 'number' ? { projectId: activeIn, hostname: '' } : activeIn ?? null;
  const scoped = sitesInActiveDomain(options, active);
  const pool = active && scoped.length > 0 ? scoped : active ? [] : options;
  if (active && pool.length === 0) {
    // The viewed domain has no connected site: say so; never list other domains.
    return hint ? { kind: 'out_of_domain' } : { kind: 'none' };
  }
  const knownPaths = Array.from(new Set(options.map((o) => pathOfName(o.name)).filter((p) => p !== '')));
  const parsed: ParsedSiteHint = hint ? parseSiteHint(hint, knownPaths) : { stem: '', path: '', wantRoot: false, namedDomain: false };
  if (!hint || parsed.stem === '') {
    const hosts = new Set(pool.map((o) => hostOfName(o.name)));
    if (hosts.size <= 1) return pickWithinDomain(pool, parsed);
    // Unscoped (no active domain) with several domains and no host named: ask, all shown.
    if (pool.length === 1) return { kind: 'one', site: pool[0] };
    return { kind: 'choose', options: pool };
  }

  // Score every host in the WORKSPACE, so a name that belongs to another domain is
  // recognised as such (and refused) instead of being forced onto the active one.
  const hostScore = new Map<string, number>();
  for (const o of options) {
    const h = hostOfName(o.name);
    if (!hostScore.has(h)) hostScore.set(h, stemSimilarity(parsed.stem, hostStem(h)));
  }
  const poolHosts = new Set(pool.map((o) => hostOfName(o.name)));
  let bestIn = 0; let bestInHost = '';
  let bestOut = 0;
  for (const [h, sc] of hostScore) {
    if (poolHosts.has(h)) { if (sc > bestIn) { bestIn = sc; bestInHost = h; } }
    else if (sc > bestOut) bestOut = sc;
  }
  if (bestIn >= SITE_MATCH_THRESHOLD && bestIn >= bestOut) {
    return pickWithinDomain(pool.filter((o) => hostOfName(o.name) === bestInHost), parsed);
  }
  if (bestOut >= SITE_MATCH_THRESHOLD) return active ? ({ kind: 'out_of_domain', otherDomain: true } as SiteResolution) : { kind: 'choose', options };
  // A specific domain that matches no site of the viewed domain ("facebook.com") is
  // refused; a vague phrase ("the new one") chooses among the viewed domain's sites.
  if (active && parsed.namedDomain) return { kind: 'out_of_domain' };
  return { kind: 'choose', options: pool };
}

/** Plain-English label for a card's state (never the full body). Names the site when known. */
export function cardStateLabel(state: string, siteName = ''): string {
  if (state === 'on_site_as_draft') return siteName ? alreadyDraftLabel(siteName) : 'Already on the site as a draft';
  return 'Not on the site yet';
}

/** Plain-English label for a per-item result. */
export function itemResultLabel(status: string, reason: string): string {
  if (status === 'published') return 'Published';
  if (status === 'drafted') return reason && reason !== 'ok' ? 'Saved as a draft (publish not allowed)' : 'Saved as a draft';
  if (status === 'skipped') return 'Already live — left as is';
  return 'Failed — ' + (reason || 'unknown error').replace(/_/g, ' ');
}

// ---- shared view model (rendered by BatchPublishPanel in chat AND voice) ----

export interface BatchViewCard {
  draft_id: number;
  title: string;
  excerpt: string;
  focus_keyword: string;
  target_page: string;
  state: string;
}

export interface BatchViewItemResult {
  draft_id: number;
  status: string;
  reason: string;
  publication_id: number | null;
  undo_available: boolean;
  reverted?: boolean;
}

export interface BatchPublishView {
  /** 'forbidden' (C6b): role may not use the feature — only the line is shown; no cards, no buttons. */
  phase: 'loading' | 'choose_site' | 'asking' | 'working' | 'done' | 'cancelled' | 'none' | 'error' | 'forbidden';
  channel: BatchChannel;
  sites: BatchSiteOption[];
  site: { id: number; name: string; label: string } | null;
  items: BatchViewCard[];
  publishAvailable: boolean;
  publishMessage: string;
  /** the proposal / status line shown above the cards (also what voice speaks). */
  line: string;
  /** the server's read-back / result lines once a confirm ran. */
  readback: string;
  resultLine: string;
  results: Record<number, BatchViewItemResult>;
  error: string;
  /** Re-push guard: drafts already LIVE on this site (labelled; the only action is "Update live post"). */
  alreadyLive?: { draft_id: number; title: string }[];
  /** Re-push guard: the open "update it?" question for cards already on the site as a WP
   *  draft (asked by voice/chat read-back OR by a tap). A tap/yes that matches it confirms. */
  updateAsk?: BatchUpdateAsk | null;
  /** Connector 1.3.0: the one live post being updated (separate read-back + pure yes / tap). */
  liveUpdate?: BatchLiveUpdateView | null;
}

export interface BatchUpdateAsk { mode: BatchMode; draftIds: number[] }

export function sameUpdateAsk(a: BatchUpdateAsk | null | undefined, b: BatchUpdateAsk | null | undefined): boolean {
  if (!a || !b || a.mode !== b.mode || a.draftIds.length !== b.draftIds.length) return false;
  const x = [...a.draftIds].sort((p, q) => p - q);
  const y = [...b.draftIds].sort((p, q) => p - q);
  return x.every((v, i) => v === y[i]);
}

/**
 * Re-push guard on the TAP path (owner decision 12-10-2026 15:29 UTC: "Already a WordPress
 * draft: Aura asks 'Already pushed as a draft. Update it?' before overwriting"; Fable IVG BA
 * advisory A-BA3). A tap on Draft/Publish (per card or "all") that would overwrite a card
 * already on the site as a WP draft returns the update question instead of confirming --
 * unless that same question (same effective mode + ids) is already open, in which case the
 * tap IS the answer and null is returned. Pure; the hook speaks/shows the line.
 */
export function tapUpdateAsk(
  view: Pick<BatchPublishView, 'site' | 'items' | 'results' | 'publishAvailable' | 'updateAsk'>,
  mode: BatchMode,
  draftIds?: number[],
): { line: string; ask: BatchUpdateAsk } | null {
  if (!view.site) return null;
  const ids = draftIds && draftIds.length > 0 ? draftIds : pendingDraftIds(view);
  const chosen = view.items.filter((i) => ids.includes(i.draft_id) && !view.results[i.draft_id]);
  if (!chosen.some((i) => i.state === 'on_site_as_draft')) return null;
  const rb = batchReadbackLine(mode, chosen, view.site.name, view.publishAvailable);
  const ask: BatchUpdateAsk = { mode: rb.mode, draftIds: chosen.map((i) => i.draft_id) };
  if (sameUpdateAsk(view.updateAsk, ask)) return null;
  return { line: rb.line, ask };
}

/** Button text for the open update question. */
export function updateAskButtonLabel(mode: BatchMode): string {
  return mode === 'publish' ? 'Update and publish' : 'Update draft';
}

export interface BatchLiveUpdateView {
  draftId: number;
  title: string;
  phase: 'proposing' | 'confirm' | 'working' | 'done' | 'undone' | 'failed' | 'cancelled';
  /** the server's read-back ("Update the live post '<title>' on <site>?"). */
  readback: string;
  resultLine: string;
  /** the update_live batch to undo (server holds the snapshot id). */
  batchId: number | null;
  undoAvailable: boolean;
}

export function emptyBatchView(channel: BatchChannel): BatchPublishView {
  return {
    phase: 'loading', channel, sites: [], site: null, items: [], publishAvailable: false,
    publishMessage: '', line: '', readback: '', resultLine: '', results: {}, error: '',
  };
}

/** C6b: the view for a role that may not use the feature — the brief line only, nothing actionable. */
export function forbiddenBatchView(channel: BatchChannel): BatchPublishView {
  return { ...emptyBatchView(channel), phase: 'forbidden', line: BATCH_OWNER_OPERATOR_ONLY };
}

/**
 * State of the "Publish" / "Publish all" buttons (cosmetic mirror of the server's
 * publish.available — C6 role gate or site policy). Disabled while busy or when the
 * server says publish is unavailable; the title carries the server's plain reason.
 */
export function publishControlState(
  view: Pick<BatchPublishView, 'phase' | 'publishAvailable' | 'publishMessage'>,
): { disabled: boolean; title: string | undefined } {
  const busy = view.phase === 'working' || view.phase === 'loading';
  return {
    disabled: busy || !view.publishAvailable,
    title: view.publishAvailable ? undefined : view.publishMessage || 'Publishing is not available for this site.',
  };
}

/** Cards still awaiting a choice (no result yet) — what "Publish all"/"Draft all" act on. */
export function pendingDraftIds(view: Pick<BatchPublishView, 'items' | 'results'>): number[] {
  return view.items.filter((c) => !view.results[c.draft_id]).map((c) => c.draft_id);
}

/** Merge an execute result's per-item outcomes into the view. */
export function mergeBatchResults(
  view: BatchPublishView,
  items: { draft_id: number; status: string; reason: string; publication_id: number | null; undo_available: boolean }[],
): Record<number, BatchViewItemResult> {
  const next = { ...view.results };
  for (const it of items) next[it.draft_id] = { ...it };
  return next;
}
